import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DecisionForm = ({ journeyId, token, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    experiment_results: '',
    hypothesis_confirmed: 'yes',
    key_learning: '',
    decision: 'continue',
    next_action: 'experiment_v2',
    next_action_details: '',
    rationale: '',
    notify_team: true,
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [journeyData, setJourneyData] = useState(null);

  // Fetch journey data to show linked hypothesis
  useEffect(() => {
    const fetchJourneyData = async () => {
      try {
        const response = await axios.get(
          `http://localhost:5000/api/journeys/${journeyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setJourneyData(response.data.journey);
      } catch (err) {
        console.error('Error fetching journey:', err);
      }
    };
    fetchJourneyData();
  }, [journeyId, token]);

  const hypothesisOptions = [
    { value: 'yes', label: 'YES — We proved it' },
    { value: 'partial', label: 'PARTIAL — Some evidence' },
    { value: 'no', label: 'NO — We disproved it' },
  ];

  const decisionOptions = [
    { value: 'continue', label: 'CONTINUE — Move forward with next experiment' },
    { value: 'pivot', label: 'PIVOT — Change our approach' },
    { value: 'stop', label: 'STOP — Close this idea for now' },
  ];

  const nextActionOptions = [
    { value: 'experiment_v2', label: 'Create Experiment v2' },
    { value: 'update_framing', label: 'Update Framing' },
    { value: 'design_phase', label: 'Move to Design Phase' },
    { value: 'another_idea', label: 'Explore Another Idea' },
    { value: 'archive', label: 'Archive & Document Learnings' },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const isFormValid = () => {
    const baseRequirements =
      formData.experiment_results.trim().length > 0 &&
      formData.hypothesis_confirmed &&
      formData.key_learning.trim().length >= 10 &&
      formData.decision &&
      formData.rationale.trim().length >= 20;

    // If continuing, next action details are also required
    const nextStepsRequirements =
      formData.decision === 'continue'
        ? formData.next_action_details.trim().length >= 10
        : true;

    return baseRequirements && nextStepsRequirements;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        hypothesis_confirmed: formData.hypothesis_confirmed,
        key_learning: formData.key_learning.trim(),
        decision: formData.decision,
        next_action: formData.next_action,
        next_action_details: formData.next_action_details.trim(),
        rationale: formData.rationale.trim(),
        notify_team: formData.notify_team,
      };

      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/decisions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccessMessage('Decision recorded successfully! Journey milestone complete.');
      if (onSuccess) {
        setTimeout(() => onSuccess(response.data.decision), 1500);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to record decision. Please try again.';
      setErrorMessage(errorMsg);
      console.error('Decision submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      experiment_results: '',
      hypothesis_confirmed: 'yes',
      key_learning: '',
      decision: 'continue',
      next_action: 'experiment_v2',
      next_action_details: '',
      rationale: '',
      notify_team: true,
    });
  };

  return (
    <div className="form-card">
      <h2 className="form-title">Record Decision</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Step 5 of 5: Document your experiment results and decision
      </p>

      {successMessage && <div className="form-success">{successMessage}</div>}
      {errorMessage && <div className="form-error">{errorMessage}</div>}

      {/* Hypothesis Context Section */}
      {journeyData && (
        <div className="form-section-header">
          <div style={{ background: '#f0f9f7', padding: '20px', borderRadius: '8px', marginBottom: '30px', borderLeft: '4px solid #8FC5B2' }}>
            <p style={{ fontSize: '12px', color: '#8FC5B2', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '8px' }}>
              Journey Context
            </p>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#2c3e50' }}>
              {journeyData.title}
            </h3>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              Current Stage: <strong>{journeyData.current_stage?.toUpperCase()}</strong>
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Experiment Results Section */}
        <div style={{ background: '#f9faf9', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#2c3e50' }}>📊 Experiment Results</h3>

          <div className="form-group">
            <label>What Happened? (Experiment Results) *</label>
            <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>
              Summarize what you observed and learned from the experiment
            </p>
            <textarea
              name="experiment_results"
              value={formData.experiment_results}
              onChange={handleInputChange}
              placeholder="e.g., 'Conducted 8 user interviews. 7 of 8 users cited format confusion as primary pain point. Users also mentioned need for error messages.'"
              maxLength="2000"
              style={{ minHeight: '100px' }}
            />
            <div className="char-count">
              {formData.experiment_results.length}/2000 characters
            </div>
          </div>
        </div>

        {/* Decision Section */}
        <div style={{ background: '#f9faf9', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#2c3e50' }}>🎯 Hypothesis Confirmation & Decision</h3>

          {/* Hypothesis Confirmation */}
          <div className="form-group">
            <label>Was the Hypothesis Confirmed? *</label>
            <div className="radio-group">
            {hypothesisOptions.map((option) => (
              <div key={option.value} className="radio-option">
                <input
                  type="radio"
                  name="hypothesis_confirmed"
                  value={option.value}
                  checked={formData.hypothesis_confirmed === option.value}
                  onChange={handleInputChange}
                />
                <label>{option.label}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Decision */}
        <div className="form-group">
          <label>What's Your Decision? *</label>
          <div className="radio-group">
            {decisionOptions.map((option) => (
              <div key={option.value} className="radio-option">
                <input
                  type="radio"
                  name="decision"
                  value={option.value}
                  checked={formData.decision === option.value}
                  onChange={handleInputChange}
                />
                <label>{option.label}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Key Learning */}
        <div className="form-group">
          <label>Key Learning (Required) *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>
            What's the most important thing you learned? (min 10 chars)
          </p>
          <textarea
            name="key_learning"
            value={formData.key_learning}
            onChange={handleInputChange}
            placeholder="Format guidance is necessary but not sufficient. Users also need validation error messages to complete the flow successfully."
            maxLength="1000"
            style={{ minHeight: '80px' }}
          />
          <div className="char-count">
            {formData.key_learning.length}/1000 characters
          </div>
        </div>

        {/* Decision Rationale */}
        <div className="form-group">
          <label>Decision Rationale (Required) *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>
            Why are you making this decision? (min 20 chars)
          </p>
          <textarea
            name="rationale"
            value={formData.rationale}
            onChange={handleInputChange}
            placeholder="Strong signal from 7 of 8 users confirms the core hypothesis. The learning about error messages broadens our solution scope. Continuing v2 experiment is justified because the core pain is validated. If v2 also confirms error-handling impact, we have high confidence to move to design."
            maxLength="2000"
            style={{ minHeight: '100px' }}
          />
          <div className="char-count">
            {formData.rationale.length}/2000 characters
          </div>
        </div>
        </div>

        {/* Next Steps Section (shows only if Continue is selected) */}
        {formData.decision === 'continue' && (
          <div style={{ background: '#f9faf9', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#2c3e50' }}>📋 Next Steps</h3>

            {/* Next Action */}
        <div className="form-group">
          <label>Next Action *</label>
          <select
            name="next_action"
            value={formData.next_action}
            onChange={handleInputChange}
            required
          >
            <option value="">Select next step...</option>
            {nextActionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Next Action Details */}
        <div className="form-group">
          <label>Next Action Details (Required) *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>
            What specifically will happen next? Who owns it? (min 10 chars)
          </p>
          <textarea
            name="next_action_details"
            value={formData.next_action_details}
            onChange={handleInputChange}
            placeholder="James (Engineer) will design Experiment v2 to test error-handling approach. Target: Start testing by Friday. Success: 80%+ users can complete flow with error messages visible."
            maxLength="1000"
            style={{ minHeight: '80px' }}
          />
          <div className="char-count">
            {formData.next_action_details.length}/1000 characters
          </div>
        </div>
        </div>
        )}

        {/* Notify Team */}
        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              name="notify_team"
              checked={formData.notify_team}
              onChange={handleInputChange}
              id="notify_team"
            />
            <label htmlFor="notify_team" style={{ margin: 0 }}>
              Notify team about this decision
            </label>
          </div>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginTop: '8px' }}>
            Team members will receive an email with the decision and rationale
          </p>
        </div>

        {/* Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </button>
          {onCancel && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? <span className="loading">⏳</span> : 'Record Decision'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DecisionForm;
